/**
 * @fileoverview Filter Validation Hook
 * 
 * This hook provides validation functionality for filter values and configurations.
 * 
 * Requirements: 3.1, 3.2
 */

import { useCallback } from 'react';

/**
 * Filter Validation Hook
 * 
 * @param {FilterConfig} config - Filter configuration
 * @returns {Object} Validation methods
 */
export const useFilterValidation = (config) => {
  /**
   * Validate a filter value against its definition
   */
  const validateFilterValue = useCallback((filterDefinition, value) => {
    const errors = [];

    // Skip validation for empty values (they will be filtered out)
    if (value === null || value === undefined || value === '') {
      return { valid: true, errors: [] };
    }

    // Type-specific validation
    switch (filterDefinition.type) {
      case 'text':
        if (typeof value !== 'string') {
          errors.push('Text filter value must be a string');
        }
        break;

      case 'select':
        if (filterDefinition.options) {
          const validValues = filterDefinition.options.map(opt => opt.value);
          
          if (Array.isArray(value)) {
            // Multi-select validation
            const invalidValues = value.filter(v => !validValues.includes(v));
            if (invalidValues.length > 0) {
              errors.push(`Invalid select values: ${invalidValues.join(', ')}`);
            }
          } else {
            // Single select validation
            if (!validValues.includes(value)) {
              errors.push(`Invalid select value: ${value}`);
            }
          }
        }
        break;

      case 'number-range':
        if (Array.isArray(value)) {
          // Range validation
          if (value.length !== 2) {
            errors.push('Number range must have exactly 2 values');
          } else {
            const [min, max] = value;
            if (typeof min !== 'number' || typeof max !== 'number') {
              errors.push('Number range values must be numbers');
            } else if (min > max) {
              errors.push('Number range minimum cannot be greater than maximum');
            }
          }
        } else {
          // Single number validation
          if (typeof value !== 'number' || isNaN(value)) {
            errors.push('Number filter value must be a valid number');
          }
        }
        break;

      case 'date-range':
        if (Array.isArray(value)) {
          // Date range validation
          if (value.length !== 2) {
            errors.push('Date range must have exactly 2 values');
          } else {
            const [start, end] = value;
            const startDate = start ? new Date(start) : null;
            const endDate = end ? new Date(end) : null;
            
            if (start && (!startDate || isNaN(startDate.getTime()))) {
              errors.push('Invalid start date');
            }
            if (end && (!endDate || isNaN(endDate.getTime()))) {
              errors.push('Invalid end date');
            }
            if (startDate && endDate && startDate > endDate) {
              errors.push('Start date cannot be after end date');
            }
          }
        } else {
          // Single date validation
          const date = new Date(value);
          if (!date || isNaN(date.getTime())) {
            errors.push('Invalid date value');
          }
        }
        break;

      case 'boolean':
        if (typeof value !== 'boolean') {
          errors.push('Boolean filter value must be true or false');
        }
        break;

      default:
        // Unknown filter type
        errors.push(`Unknown filter type: ${filterDefinition.type}`);
    }

    // Apply validation rules if present
    if (filterDefinition.validation && errors.length === 0) {
      for (const rule of filterDefinition.validation) {
        const ruleError = validateRule(value, rule);
        if (ruleError) {
          errors.push(ruleError);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, []);

  /**
   * Validate a single validation rule
   */
  const validateRule = useCallback((value, rule) => {
    switch (rule.type) {
      case 'required':
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return rule.message;
        }
        break;

      case 'minLength':
        if (typeof value === 'string' && value.length < rule.value) {
          return rule.message;
        }
        break;

      case 'maxLength':
        if (typeof value === 'string' && value.length > rule.value) {
          return rule.message;
        }
        break;

      case 'min':
        if (typeof value === 'number' && value < rule.value) {
          return rule.message;
        }
        break;

      case 'max':
        if (typeof value === 'number' && value > rule.value) {
          return rule.message;
        }
        break;

      case 'pattern':
        if (typeof value === 'string' && !new RegExp(rule.value).test(value)) {
          return rule.message;
        }
        break;

      case 'custom':
        if (rule.validator && typeof rule.validator === 'function') {
          if (!rule.validator(value)) {
            return rule.message;
          }
        }
        break;

      default:
        console.warn(`Unknown validation rule type: ${rule.type}`);
    }

    return null;
  }, []);

  /**
   * Validate an array of active filters
   */
  const validateActiveFilters = useCallback((activeFilters, filterDefinitions) => {
    const errors = [];
    const warnings = [];

    if (!Array.isArray(activeFilters)) {
      errors.push('Active filters must be an array');
      return { valid: false, errors, warnings };
    }

    for (const filter of activeFilters) {
      // Find the filter definition
      const definition = filterDefinitions.find(def => def.id === filter.id);
      if (!definition) {
        warnings.push(`No definition found for filter: ${filter.id}`);
        continue;
      }

      // Validate the filter value
      const validation = validateFilterValue(definition, filter.value);
      if (!validation.valid) {
        errors.push(`Filter ${filter.id}: ${validation.errors.join(', ')}`);
      }

      // Validate operator
      if (filter.operator) {
        const validOperators = definition.operators || [];
        const isValidOperator = validOperators.some(op => op.type === filter.operator.type);
        if (!isValidOperator) {
          errors.push(`Invalid operator for filter ${filter.id}: ${filter.operator.type}`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }, [validateFilterValue]);

  /**
   * Validate filter configuration
   */
  const validateFilterConfig = useCallback((filterConfig) => {
    const errors = [];

    if (!filterConfig || typeof filterConfig !== 'object') {
      errors.push('Filter config must be an object');
      return { valid: false, errors };
    }

    // Validate required fields
    if (!filterConfig.context) {
      errors.push('Context is required');
    }

    if (!filterConfig.filters || !Array.isArray(filterConfig.filters)) {
      errors.push('Filters must be an array');
    } else {
      // Validate each filter definition
      filterConfig.filters.forEach((filter, index) => {
        if (!filter.id) {
          errors.push(`Filter ${index}: ID is required`);
        }
        if (!filter.type) {
          errors.push(`Filter ${index}: Type is required`);
        }
        if (!filter.label) {
          errors.push(`Filter ${index}: Label is required`);
        }
      });
    }

    // Validate layout
    if (filterConfig.layout && !['horizontal', 'vertical', 'dropdown'].includes(filterConfig.layout)) {
      errors.push('Layout must be one of: horizontal, vertical, dropdown');
    }

    // Validate callbacks
    if (filterConfig.onFilterChange && typeof filterConfig.onFilterChange !== 'function') {
      errors.push('onFilterChange must be a function');
    }

    if (filterConfig.onFilterReset && typeof filterConfig.onFilterReset !== 'function') {
      errors.push('onFilterReset must be a function');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, []);

  /**
   * Validate filter preset
   */
  const validateFilterPreset = useCallback((preset, filterDefinitions) => {
    const errors = [];

    if (!preset || typeof preset !== 'object') {
      errors.push('Preset must be an object');
      return { valid: false, errors };
    }

    // Validate required fields
    if (!preset.id) {
      errors.push('Preset ID is required');
    }
    if (!preset.name) {
      errors.push('Preset name is required');
    }
    if (!preset.filters || !Array.isArray(preset.filters)) {
      errors.push('Preset filters must be an array');
    } else {
      // Validate preset filters
      const filterValidation = validateActiveFilters(preset.filters, filterDefinitions);
      if (!filterValidation.valid) {
        errors.push(...filterValidation.errors);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, [validateActiveFilters]);

  /**
   * Sanitize filter value to ensure it's safe for use
   */
  const sanitizeFilterValue = useCallback((value, filterType) => {
    if (value === null || value === undefined) {
      return null;
    }

    switch (filterType) {
      case 'text':
        // Remove potentially dangerous characters and trim
        return String(value).trim().replace(/[<>]/g, '');

      case 'select':
        // Ensure select values are safe
        if (Array.isArray(value)) {
          return value.map(v => String(v).trim());
        }
        return String(value).trim();

      case 'number-range':
        if (Array.isArray(value)) {
          return value.map(v => {
            const num = Number(v);
            return isNaN(num) ? 0 : num;
          });
        }
        const num = Number(value);
        return isNaN(num) ? 0 : num;

      case 'date-range':
        if (Array.isArray(value)) {
          return value.map(v => {
            if (!v) return null;
            const date = new Date(v);
            return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
          });
        }
        if (!value) return null;
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];

      case 'boolean':
        return Boolean(value);

      default:
        return value;
    }
  }, []);

  return {
    validateFilterValue,
    validateActiveFilters,
    validateFilterConfig,
    validateFilterPreset,
    sanitizeFilterValue
  };
};
